// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ArtifactListCard.test.tsx
import { CircleNotch } from "@phosphor-icons/react";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import { UnreadDot } from "../../../components/ask-puntt-list/UnreadDot";
import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";
import { type ChatArtifact } from "../artifactTypes";

/** Soft shadow-3 — matches ReviewListCard / Figma ArtifactListCard 49613:313. */
const ARTIFACT_CARD_CHROME =
  "rounded-xl border border-puntt-neutral-gray-7 bg-base-white shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]";

/** Canvas / document row in the artifact sidebar (reviews use ReviewListCard). */
export function ArtifactListCard({
  artifact,
  isActive,
  onClick,
  testIdPrefix = "chat-artifact-row",
  hasUnread = false,
  unreadTestId,
  "data-testid": dataTestId,
}: {
  artifact: ChatArtifact;
  isActive?: boolean;
  onClick?: () => void;
  testIdPrefix?: string;
  hasUnread?: boolean;
  unreadTestId?: string;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const disabled = artifact.isGenerating ?? false;
  const cardTestId = dataTestId ?? `${testIdPrefix}-${artifact.id}`;

  const ariaLabel = disabled
    ? t("artifactListCard.generatingAriaLabel", { title: artifact.title })
    : isActive
      ? t("artifactListCard.currentlyViewingAriaLabel", {
          title: artifact.title,
        })
      : t("artifactListCard.openAriaLabel", { title: artifact.title });

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-current={isActive ? "true" : undefined}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
      aria-label={ariaLabel}
      data-testid={cardTestId}
      data-artifact-kind={artifact.kind}
      data-artifact-active={isActive ? "true" : "false"}
      className={cx(
        "flex w-full flex-col gap-1 p-2 text-left disabled:cursor-not-allowed disabled:opacity-60",
        ARTIFACT_CARD_CHROME,
        !disabled && "hover:border-puntt-accent-7",
        isActive && "ring-2 ring-puntt-accent-8 ring-offset-1",
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span className="block min-w-0 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
          {artifact.title}
        </span>
        {disabled ? (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-neutral-gray-9"
            aria-hidden="true"
            data-testid={`${cardTestId}-icon`}
          />
        ) : null}
        {hasUnread ? (
          <UnreadDot
            testId={unreadTestId ?? `chat-artifact-unread-${artifact.id}`}
            ariaLabel={t("artifactListCard.newDocumentAriaLabel")}
          />
        ) : null}
      </span>
      {artifact.versionNumber != null || artifact.subtitle ? (
        <span className="flex flex-wrap items-center gap-2">
          {artifact.versionNumber != null ? (
            <span
              className="inline-block rounded-sm bg-puntt-neutral-gray-3 px-2 py-0.5 text-[12px] font-medium text-puntt-neutral-gray-11"
              data-testid={`${cardTestId}-version`}
            >
              V{artifact.versionNumber}
            </span>
          ) : null}
          {artifact.subtitle ? (
            <span
              className="inline-flex items-center text-[11px] text-puntt-neutral-gray-10"
              data-testid={`${cardTestId}-subtitle`}
            >
              {artifact.subtitle}
            </span>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}
